/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/KubernetesEndpoint/Components/EndpointDetailsComponent" />
import * as React from "react";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { EndpointsEditorPanelUtils, cloneEndpoint } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { InputDataType } from "ServiceEndpoints/Client/Contracts/FormInput_ServiceEndpoint";

import { FormItem } from "VSSUI/FormItem";
import { TextField } from "VSSUI/TextField";
import { Checkbox } from "VSSUI/Checkbox";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { InputDetails } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { EndpointEditorBaseComponent, IEndpointEditorBaseComponentState, IEndpointEditorBaseComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointEditorBaseComponent";
import { css } from "VSSUI/Util";

export interface IEndpointDetailsComponentProps extends IEndpointEditorBaseComponentProps {
    updateValidAndDirtyStateChange: (isValid?: boolean, isDirty?: boolean) => void;
}

export class EndpointDetailsComponent extends EndpointEditorBaseComponent<IEndpointDetailsComponentProps, IEndpointEditorBaseComponentState>{
    constructor(props: IEndpointDetailsComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);
        this._inputDetailsList = [
            {
                inputId: this._nameInputId,
                initialValue: props.draftEndpoint.name,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, true),
                isConfidential: false,
                isUpdate: this.props.isUpdateForm
            },
            {
                inputId: this._descriptionInputId,
                initialValue: props.draftEndpoint.description,
                inputValidation: EndpointsEditorPanelUtils.getInputValidation(InputDataType.String, false),
                isConfidential: false,
                isUpdate: this.props.isUpdateForm
            }
        ];
    }

    public componentDidMount() {
        this.addInputs(this._inputDetailsList);
    }

    public componentWillReceiveProps(props: IEndpointDetailsComponentProps) {
        this._draftServiceEndpoint = cloneEndpoint(props.draftEndpoint);
    }

    public componentWillUnmount() {
        let currentInputIds: string[] = [];
        this._inputDetailsList.forEach((input) => currentInputIds.push(input.inputId));
        this.removeInputs(currentInputIds);
    }

    public render(): React.ReactNode {
        return (
            <div>
                <div className="service-connection-details-section">
                    {!this.props.disableDetailsHeader && <span role="heading" aria-level={4} className="service-endpoint-section-headers">{Resources.DetailsHeader}</span>}
                    <FormItem
                        label={Resources.NameInputLabel}
                        className="default-input-margin-top"
                        message={this._getErrorMessage(this._nameInputId)}
                        error={this._hasError(this._nameInputId)}
                    >
                        <TextField
                            value={this._draftServiceEndpoint.name}
                            onChange={this._onNameChange}
                        />
                    </FormItem>
                    <FormItem
                        label={Resources.DescriptionInputLabel}
                        className="default-input-margin-top"
                        message={this._getErrorMessage(this._descriptionInputId)}
                        error={this._hasError(this._descriptionInputId)}
                    >
                        <TextField
                            value={this._draftServiceEndpoint.description}
                            onChange={this._onDescriptionChange}
                            multiline={true}
                        />
                    </FormItem>
                    <div className="pipeline-permissions-section default-input-margin-top">
                        <div className={css("bolt-formitem-label body-m", this.props.titleClassName)}>{Resources.SecurityHeader}</div>
                        <Checkbox
                            onChange={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
                                this.props.updateAllowAllPipelinesFlag(checked);
                            }}
                            checked={this.props.allowAllPipelinesFlagState}
                            label={Resources.PipelinePermissionsCheckboxLabel}
                            disabled={this.props.disableAllPipelinesSetting}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private _hasError(inputId: string): boolean {
        return (!!this.getInputError(inputId));
    }

    private _getErrorMessage(inputId: string): string {
        return this.getInputError(inputId) || "";
    }

    private _onNameChange = (event: any, value: string) => {
        this._draftServiceEndpoint.name = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._nameInputId, value);
    }

    private _onDescriptionChange = (event: any, value: string) => {
        this._draftServiceEndpoint.description = value;
        this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        this.validateInputState(this._descriptionInputId, value);
    }

    protected handleEnableSaveStateChange(enableSave: boolean) {
        this.props.updateInvalidInputsCount(enableSave ? -1 : 1);
    }

    protected handleValidAndDirtyStateChange(isValid?: boolean, isDirty?: boolean) {
        this.props.updateValidAndDirtyStateChange(isValid, isDirty);
    }

    private _draftServiceEndpoint: ServiceEndpoint;
    private _nameInputId = "endpoint-name";
    private _descriptionInputId = "endpoint-description";
    private _inputDetailsList: InputDetails[];
}
